import getPriorGtagEntries from 'utils/getPriorGtagEntries'
import isDomainMatch from 'utils/isDomainMatch'
import logLateConsentDiagnostic from 'utils/logLateConsentDiagnostic'


export default {
  // We're defaulting to an empty string so that we don't have to resort to using
  // alternations (apiBase || '') where this value is actually used. This is helpful/
  // necessary when we're importing resource-blocker into the dashboard, where we
  // don't get to leverage the results of utils/getConfig().
  apiBase: '',
  websiteUUID: '',

  consentOrigin: void 0,

  _autoBlockerOverrides: {},

  _loadedCategories: new Set(),

  _unblockedDomains: [],

  addUnblockedDomain(domain) {
    this._unblockedDomains.push(domain)
  },

  /**
   * Anything recorded here has been allowed to run and cannot be un-run. That
   * is what separates a consent change we can enforce in place from one that
   * needs a full page reload.
   *
   * @param {string[]} categories
   */
  addLoadedCategories(categories) {
    // A `data-categories` of "advertising, analytics" is split on the comma
    // alone, so the whitespace a site maintainer wrote comes along with it.
    // Recorded as-is it would never match the category we look it up by.
    for ( const category of categories ) {
      this._loadedCategories.add(category.trim())
    }
  },

  /**
   * @param {string} category
   *
   * @returns {boolean} true if something answering to `category` has already
   *   been allowed to load
   */
  hasLoadedCategory(category) {
    return this._loadedCategories.has(category)
  },

  // Called by `sendGCMConsentDefaults` right after it pushes
  // `gtag('consent', 'default', ...)`. Freezes the diagnostic verdict
  // as of push time. The verdict mirrors Google's own late-consent
  // signal so we agree with Google regardless of whether tags fired
  // via direct gtag.js or via GTM (which doesn't push array-form
  // `gtag('config'|'event', ...)` entries to the dataLayer at all).
  // `priorGtagEntries` is kept as a diagnostic aid for the direct
  // gtag.js case where we can actually enumerate what fired.
  //
  captureLateConsentSnapshot({ hasWaitForUpdate }) {
    this._lateConsentSnapshot = {
      hasWaitForUpdate,
      isLate: Boolean(window.google_tag_data?.ics?.wasSetLate),
      priorGtagEntries: getPriorGtagEntries(),
    }
  },

  // Expected entry point for customers using Google's CMP Partner
  // Program "debug mode" diagnostic: they open the browser dev console
  // and run `Termly.checkConsentLoadOrder()` to find out whether Google
  // tags fired before Termly set its GCM consent defaults.
  //
  checkConsentLoadOrder() {
    if ( window.TERMLY_FORCE_DISABLE_GCM ) {
      console.info('[Termly] GCM is disabled on this page (found window.TERMLY_FORCE_DISABLE_GCM = %o). '
                   + 'Late-consent detection does not apply.',
                   window.TERMLY_FORCE_DISABLE_GCM)
      return
    }

    logLateConsentDiagnostic(this._lateConsentSnapshot)
  },

  // Machine-readable counterpart to `checkConsentLoadOrder()` intended
  // for the Termly scanner. Returns:
  //
  // - `null`  — GCM consent defaults have not been sent (e.g. Termly
  //             hasn't initialized, or `TERMLY_FORCE_DISABLE_GCM` is
  //             set). The scanner should treat this as distinct from
  //             "on time."
  // - `true`  — defaults were sent, but Google tags fired first.
  // - `false` — defaults were sent before any Google tags fired.
  //
  isGCMConsentLate() {
    return this._lateConsentSnapshot?.isLate ?? null
  },

  matchesUnblockedDomain(hostname) {
    return this._unblockedDomains.some((domain) => {
      return isDomainMatch(domain, hostname)
    })
  },

  pauseAutoBlocker() {
    this._isAutoBlockerPaused = true
  },

  resumeAutoBlocker() {
    this._isAutoBlockerPaused = false
  },

  isAutoBlockerPaused() {
    return Boolean(this._isAutoBlockerPaused)
  },

  wasAutoBlockerInstalled() {
    return Boolean(this._autoBlockerOverrides.install)
  },

  getDefaultConsents() {
    return this._defaultConsents
  },

  getReleaseDecision() {
    return this._releaseDecision
  },

  removeAutoBlockerOverrides() {
    this._autoBlockerOverrides.remove?.()
  },

  setAutoBlockerOverrides(overrides) {
    this._autoBlockerOverrides = overrides
  },

  setDefaultConsents(defaultConsents) {
    this._defaultConsents = defaultConsents
  },

  /**
   * Records the release that was last carried out, so the unblock observer can
   * extend the same decision to manually-marked tags that turn up afterward.
   * Stays `undefined` until something is released.
   *
   * @param {object} [decision] - `undefined` where nothing has been released
   * @param {string[]} [decision.categories] - the categories released;
   *   `undefined` where everything was
   */
  setReleaseDecision(decision) {
    this._releaseDecision = decision
  },

  setSupportedLanguages(languages) {
    const lcLanguages = languages.map((lang) => lang.toLowerCase())

    this._supportedLanguages = new Set(lcLanguages)
  },

  isLanguageSupported(language = '') {
    if ( !this._supportedLanguages ) {
      console.debug('Termly.isLanguageSupported() called before Termly.setSupportedLanguages()')
      return false
    }

    return this._supportedLanguages.has(language.toLowerCase())
  },
}
